import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { AiChatTurn } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import { Fragment } from "react";
import {
  resolveSlot,
  useExecutionPanelContext,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { Turn } from "./Turn";

const turnHasThoughtProcessStep = (
  turn: AiChatTurn | undefined,
  reasoningNodeAllowList?: string[]
) => {
  if (!turn) {
    return false;
  }

  return turn.nodes.some((node) =>
    node.messages.some((message) =>
      message.parts.some((part) => {
        if (part.type === "dynamic-tool") {
          return part.toolName !== "request-user-input";
        }

        if (part.type !== "reasoning" || node.type === "condition") {
          return false;
        }

        return (
          !reasoningNodeAllowList?.length ||
          reasoningNodeAllowList.includes(node.type ?? "")
        );
      })
    )
  );
};

export const TurnStream = () => {
  const {
    aiChat,
    streaming,
    debugMode,
    compact,
    showSystemMessages,
    showToolCallOutput,
    showToolsAsTasks,
    showToolsAsSteps,
    showConditionNodes,
    showReasoningMessages,
    showExpandedReasoningMessages,
    reasoningNodeAllowList,
    toolCallDefaultExpanded,
    disableResponseWordByWordLoading,
    clearReasoningOnMessage,
    hiddenTools,
    transformMessage,
    renderPart,
    layoutDensity,
    getSlot,
  } = useExecutionPanelContext();
  const turnFooter = getSlot("turnFooter");

  const turns = aiChat.turns;
  const activeTurnHasThoughtProcessStep = turnHasThoughtProcessStep(
    turns.at(-1),
    reasoningNodeAllowList
  );
  const showLoadingGem =
    streaming && (!showToolsAsSteps || !activeTurnHasThoughtProcessStep);

  // Message-rendering flags forwarded to each turn (and on to its parts). The
  // debug toggle is the derived `debugMode`, not the `debug` message flag.
  const messageFlags = {
    isStreaming: streaming,
    debug: debugMode,
    showSystemMessages,
    showToolCallOutput,
    showToolsAsTasks,
    showToolsAsSteps,
    showConditionNodes,
    showReasoningMessages,
    showExpandedReasoningMessages,
    reasoningNodeAllowList,
    toolCallDefaultExpanded,
    disableResponseWordByWordLoading,
    clearReasoningOnMessage,
    hiddenTools,
    layoutDensity,
  };

  // Renders directly into the parent's single ConversationContent
  // (StickToBottom.Content) — must not wrap its own, or the content refs nest
  // and auto-scroll breaks.
  return (
    <>
      {turns.map((turn, idx) => {
        const isLastTurn = idx === turns.length - 1;
        return (
          <Fragment key={idx}>
            <Turn
              turn={turn}
              showVisibilityReasons={true}
              isLastTurn={isLastTurn}
              transformMessage={transformMessage}
              renderPart={renderPart}
              {...messageFlags}
            />
            {resolveSlot(turnFooter, {
              turn,
              streaming: streaming && isLastTurn,
            })}
          </Fragment>
        );
      })}
      {showLoadingGem ? (
        <Box
          sx={{
            width: compact ? "72px" : "100px",
            marginLeft: compact ? "-18px" : "-30px",
          }}
        >
          <DotLottieReact
            aria-label="AI is responding"
            role="status"
            src={"/lottie/blue-diamond.json"}
            loop={true}
            autoplay={true}
            renderConfig={{
              autoResize: true,
            }}
          />
        </Box>
      ) : null}
    </>
  );
};
